import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from "react-router-dom";
import axiosInstance from './../axiosInstance.js';
import {localizeKey} from './../localize.js';
import * as oauth from './../oauth.js';
import MyUserContext from './../myUserContext.js';
import AvatarUpload from './AvatarUpload';
import ExpandableImage from './ExpandableImage';
import ToggleSwitch from './ToggleSwitch';
import TelegramLoginButton from './TelegramLoginButton';
import { Button, SegmentedControl, Stack, Group, Text, TextInput, Title, Paper, useMantineColorScheme } from '@mantine/core';

const Settings = ({ onSettingsChange, onLogout }) => {
  const myUser = useContext(MyUserContext);
  const { colorScheme, setColorScheme } = useMantineColorScheme();

  const navigate = useNavigate();

  const [selectedLanguage, setSelectedLanguage] = useState('en'); // Default language
  const [selectedUserName, setSelectedUserName] = useState(myUser.myUserSettings.userName);
  const [isLoading, setIsLoading] = useState(true); // State to manage loading state
  const [error, setError] = useState(null); // State to manage errors
  const [tgLinked, setTgLinked] = useState(!!myUser?.myUserSettings?.telegramUserId);
  const [tgLinkError, setTgLinkError] = useState(null);
  const [tgConflict, setTgConflict] = useState(null); // { conflictUsername, tgData }
  const [userNameInput, setUsernameInput] = useState(myUser.myUserSettings.userName);
  const [selectedIsPrivateAccount, setSelectedIsPrivateAccount] = useState(myUser.myUserSettings.privateAccount);
  const [selectedAutoSubscribeToFriendsChats, setSelectedAutoSubscribeToFriendsChats] = useState(
    myUser?.myUserSettings?.autoSubscribeToFriendsChats ?? true
  );


  const [userSettings, setUserSettings] = useState(null);

  // Add an interceptor to add authorization header to requests
  axiosInstance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('jwtToken'); // Get token from local storage
      if (token) {
        config.headers.Authorization = `Bearer ${token}`; // Add authorization header
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  useEffect(() => {
    // Fetch initial language setting when the component mounts
    axiosInstance
      .get('/user/settings')
      .then((response) => {
        const { lang, privateAccount, autoSubscribeToFriendsChats } = response.data;
        setSelectedLanguage(lang); // TODO: do not process it independently
        setSelectedIsPrivateAccount(privateAccount);
        setSelectedAutoSubscribeToFriendsChats(autoSubscribeToFriendsChats ?? true);
        setUserSettings(response.data);
        setIsLoading(false); // Update loading state after successful response
      })
      .catch((error) => {
        setError(error.message); // Set error state with error message
        setIsLoading(false); // Update loading state after error
      });
  }, []); // Empty dependency array ensures this effect runs only once when the component mounts


  const handleLanguageChange = (newLanguage) => {
    setIsLoading(true); // Set loading state to true

    axiosInstance
    .post('/user/settings', { lang: newLanguage })
    .then((response) => {
      const { lang } = response.data;
      setSelectedLanguage(lang); // Update language state with the response
      onSettingsChange(lang, response.data.userName, response.data.avatarUrl);
      setError(null); // Clear any previous errors
    })
    .catch((error) => {
      setError(error.message); // Set error state with error message
    })
    .finally(() => {
      setIsLoading(false); // Set loading state to false regardless of success or failure
    });
  };

  const handleLinkTelegram = (tgData, confirm = false) => {
    setTgLinkError(null);
    oauth.LinkTelegram({ tgData, confirm })
      .then((result) => {
        if (result.conflict) {
          setTgConflict({ conflictUsername: result.conflictUsername, tgData });
          return;
        }
        setTgLinked(true);
        setTgConflict(null);
      })
      .catch((err) => {
        setTgLinkError(err.response?.status === 409
          ? (selectedLanguage === 'ru' ? 'Telegram уже привязан' : 'Telegram already linked')
          : (selectedLanguage === 'ru' ? 'Не удалось привязать Telegram' : 'Failed to link Telegram'));
      });
  };

  const handleUsernameInputChange = (event) => {
    setUsernameInput(event.target.value);
  };
  const handleUsernameChange = () => {
    axiosInstance
    .post('/user/settings', { userName: userNameInput })
    .then((response) => {
      setSelectedUserName(response.data.userName);
      onSettingsChange(response.data.lang, response.data.userName, response.data.avatarUrl);
      setError(null); // Clear any previous errors
    })
    .catch((error) => {
      setError(error.message); // Set error state with error message
    })
    .finally(() => {
      setIsLoading(false); // Set loading state to false regardless of success or failure
    });
  }

  const handleAccountIsPrivate = (isPrivate) => {
    axiosInstance
    .post('/user/settings', { privateAccount: isPrivate })
    .then((response) => {
      setSelectedIsPrivateAccount(isPrivate);
      setUserSettings(response.data);
      setError(null);
    })
    .catch((error) => {
      setError(error.message);
    })
    .finally(() => {
      setIsLoading(false); // TODO: get rid of it
    })
  }

  const handleAutoSubscribeToFriendsChats = (shouldAutoSubscribe) => {
    axiosInstance
    .post('/user/settings', { autoSubscribeToFriendsChats: shouldAutoSubscribe })
    .then((response) => {
      setSelectedAutoSubscribeToFriendsChats(shouldAutoSubscribe);
      setUserSettings(response.data);
      setError(null);
    })
    .catch((error) => {
      setError(error.message);
    })
    .finally(() => {
      setIsLoading(false); // TODO: get rid of it
    })
  }

  const settingsAvatarUrlChange = (url) => {
    onSettingsChange(selectedLanguage, selectedUserName, url);
  }

  const handleRecalcClick = (smart) => {
    const userId = myUser?.myUserInfo?.myUserId;
    if (!userId) {
      alert('No user ID found');
      return;
    }

    axiosInstance.post(`/user/${userId}/recalc${smart ? '?smart=true' : ''}`)
      .then(response => {
        alert('Recalculation started successfully');
      })
      .catch(error => {
        alert('Could not start recalculation');
      });
  };

  const handleUserMatchRecalcClick = () => {
    axiosInstance.post(`/userMatch/recalc`)
      .then(response => {
        alert('User match recalculation started successfully');
      })
      .catch(error => {
        alert('Could not start user match recalculation');
      });
  };

  const handleHotRecalcClick = () => {
    axiosInstance.post(`/recalc/hot`)
      .then(response => {
        alert('Hot recalculation started successfully');
      })
      .catch(error => {
        alert('Could not start hot recalculation');
      });
  };

  const language = myUser ? myUser.myUserSettings.lang : 'ru';

  if (isLoading) {
    return <div></div>;
  }

  if (error) {
    return <p>Error: {error}</p>;
  }

  return (
    <div className="container mx-auto max-w-md px-4 mt-4">
      {isLoading && <p></p>}
      {error && <Text c="red" ta="center">Error: {error}</Text>}

      <Title order={2} ta="center" mb="xl">
        {language === 'ru' ? 'Настройки' : 'Settings'}
      </Title>

      <Stack gap="xl" align="center">
        <Paper withBorder p="lg" w="100%">
          <Stack gap="md">
            <Text size="sm" fw={600}>{selectedLanguage === 'ru' ? 'Профиль' : 'Profile'}</Text>
            <Group gap="sm" justify="center">
              <TextInput
                size="sm"
                radius="md"
                value={userNameInput}
                onChange={handleUsernameInputChange}
                style={{ flex: 1 }}
              />
              <Button variant="light" size="sm" onClick={handleUsernameChange}>{localizeKey('setUserName', selectedLanguage)}</Button>
            </Group>
            <div className="flex justify-center">
              <AvatarUpload initUrl={userSettings.avatarUrl} onAvatarUrlChange={settingsAvatarUrlChange} />
            </div>
          </Stack>
        </Paper>

        <Paper withBorder p="lg" w="100%">
          <Stack gap="md">
            <Text size="sm" fw={600}>{selectedLanguage === 'ru' ? 'Язык' : 'Language'}</Text>
            <div className="flex justify-center">
              <SegmentedControl
                value={selectedLanguage}
                onChange={handleLanguageChange}
                disabled={isLoading}
                data={[
                  { label: 'English', value: 'en' },
                  { label: 'Русский', value: 'ru' },
                ]}
                radius="xl"
                size="sm"
              />
            </div>

            <Text size="sm" fw={600} mt="sm">{selectedLanguage === 'ru' ? 'Тема' : 'Theme'}</Text>
            <div className="flex justify-center">
              <SegmentedControl
                value={colorScheme}
                onChange={(value) => {
                  setColorScheme(value);
                  axiosInstance.post('/user/settings', { theme: value }).catch(() => {});
                }}
                data={[
                  { label: selectedLanguage === 'ru' ? 'Светлая' : 'Light', value: 'light' },
                  { label: selectedLanguage === 'ru' ? 'Тёмная' : 'Dark', value: 'dark' },
                  { label: 'Auto', value: 'auto' },
                ]}
                radius="xl"
                size="sm"
              />
            </div>
          </Stack>
        </Paper>

        <Paper withBorder p="lg" w="100%">
          <Stack gap="md">
            <Text size="sm" fw={600}>{selectedLanguage === 'ru' ? 'Приватность' : 'Privacy'}</Text>
            <Group gap="sm" justify="space-between">
              <div>
                <Text size="sm">{(selectedLanguage === 'ru') ? "Приватный аккаунт" : "Private account"}</Text>
                <Text size="xs" c="dimmed">
                  {selectedLanguage === 'ru' ?
                    "Только одобренные подписчики видят вашу страницу" :
                    "Only approved subscribers see your page"}
                </Text>
              </div>
              <ToggleSwitch isOn={selectedIsPrivateAccount} onToggle={handleAccountIsPrivate} />
            </Group>
            <Group gap="sm" justify="space-between">
              <div>
                <Text size="sm">{(selectedLanguage === 'ru') ? "Автоподписка на чаты" : "Auto-subscribe to chats"}</Text>
                <Text size="xs" c="dimmed">
                  {selectedLanguage === 'ru' ?
                    "Подписывать на чаты друзей автоматически" :
                    "Auto-subscribe to friends' event chats"}
                </Text>
              </div>
              <ToggleSwitch isOn={selectedAutoSubscribeToFriendsChats} onToggle={handleAutoSubscribeToFriendsChats} />
            </Group>
          </Stack>
        </Paper>
      
        <Paper withBorder p="lg" w="100%">
          <Stack gap="sm" align="center">
            <Text size="sm" fw={600}>{selectedLanguage === 'ru' ? 'Telegram' : 'Telegram'}</Text>
            {tgLinked ? (
              <Group gap="xs" justify="center" align="center">
                <Text size="sm" c="green">&#10003; {selectedLanguage === 'ru' ? 'Telegram привязан' : 'Telegram linked'}</Text>
              </Group>
            ) : tgConflict ? (
              <Stack gap="sm" align="center">
                <Text size="sm" ta="center">
                  {selectedLanguage === 'ru'
                    ? `Уже существует аккаунт WowContent для Telegram аккаунта, который вы пытаетесь привязать. После привязки вы потеряете доступ к аккаунту WowContent, который сейчас привязан к этому Telegram аккаунту. Продолжить?`
                    : `There is already existing WowContent account for the telegram account you are trying to link. After successful link, you will lose access to WowContent account which is now linked to the telegram account. Continue?`}
                </Text>
                <Group gap="sm" justify="center">
                  <Button size="sm" color="red" onClick={() => handleLinkTelegram(tgConflict.tgData, true)}>
                    {selectedLanguage === 'ru' ? 'Продолжить' : 'Continue'}
                  </Button>
                  <Button size="sm" variant="light" onClick={() => setTgConflict(null)}>
                    {selectedLanguage === 'ru' ? 'Отмена' : 'Cancel'}
                  </Button>
                </Group>
              </Stack>
            ) : (
              <>
                <Text size="sm" ta="center">
                  {selectedLanguage === 'ru'
                    ? 'Привязать Telegram (позволит получать уведомления и логиниться в том числе через Telegram)'
                    : 'Link Telegram (enables notifications and login via Telegram)'}
                </Text>
                <TelegramLoginButton onAuth={(data) => handleLinkTelegram(data)} />
                {tgLinkError && <Text size="sm" c="red">{tgLinkError}</Text>}
              </>
            )}
          </Stack>
        </Paper>

        {myUser?.myUserInfo?.myUserId === '4312f009-7502-43a6-9483-2824508fe96f' && (
          <div className="flex justify-center flex-wrap">
            <Button size="xs" color="blue" m={4} onClick={()=>{handleRecalcClick(false);}}>Recalculate</Button>
            <Button size="xs" color="green" m={4} onClick={()=>{handleRecalcClick(true);}}>Recalculate smart</Button>
            <Button size="xs" color="gray" m={4} onClick={()=>{handleHotRecalcClick();}}>Recalculate hots</Button>
            <Button size="xs" color="red" m={4} onClick={()=>{handleUserMatchRecalcClick();}}>Recalculate user match</Button>
          </div>
        )}

        <Button
          variant="subtle"
          color="red"
          size="sm"
          mt="xl"
          onClick={()=> {
            if (onLogout) {
              onLogout();
            } else {
              localStorage.removeItem('jwtToken');
            }
            navigate("/");
          }}
        >
          {language === "ru" ? "Выйти" : "Log out"}
        </Button>

        <Text size="xs" c="dimmed" ta="center" mt="xl">
          {selectedLanguage === "en"
            ? "Hint: for a better experience on mobile, add the website to your home screen."
            : "Рекомендация: для удобства на мобильных добавьте веб-сайт на домашний экран."
          }
        </Text>
        {selectedLanguage === "ru" && (
          <Stack gap="xs" align="center">
            <Group gap="xs" align="center">
              <Text size="xs" c="dimmed">Android:</Text>
              <ExpandableImage small={true} src={"https://wowcontent.ams3.cdn.digitaloceanspaces.com/add_to_home_screen_android.jpg"} />
            </Group>
            <Group gap="xs" align="center">
              <Text size="xs" c="dimmed">iOS:</Text>
              <ExpandableImage small={true} src={"https://wowcontent.ams3.cdn.digitaloceanspaces.com/add_to_home_screen_ios.jpg"} />
            </Group>
          </Stack>
        )}
      </Stack>
    </div>
  );
};

export default Settings;
